import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, styled, Typography, useMediaQuery } from '@mui/material';

import { getPctgColor } from '»utils/pctgColors';

const getHeight = ({
  isWallet,
  md,
  isOpen,
  isOrder,
  isMobile,
  isPrimary,
  isFundsPrimary,
}) => {
  if (isWallet) {
    if (isOpen) {
      if (md) {
        return { height: '232px' };
      }
      return { height: '234px' };
    }
    return { height: 'auto' };
  }
  if (isOrder) {
    return isMobile && isPrimary
      ? { minHeight: '222px' }
      : { minHeight: '128px' };
  }
  if (isOpen) {
    if (md) {
      return {
        height: '268px',
        '&:has(#divFundsPrimaryDetailWrapperMobile)': {
          height: '225px',
        },
      };
    }
    return {
      height: '336px',
      '&:has(#divFundsPrimaryDetailWrapperMobile)': {
        height: 'auto',
        minHeight: '160px',
      },
    };
  }

  if (md)
    return {
      height: '128px',
    };

  return {
    height: isFundsPrimary ? '120px' : '128px',
  };
};

export const Wrapper = styled('div', {
  shouldForwardProp: (prop) =>
    prop !== 'isOpen' &&
    prop !== 'isWallet' &&
    prop !== 'isOrder' &&
    prop !== 'isStockExchangeMarket' &&
    prop !== 'isPrimary' &&
    prop !== 'isHistoric' &&
    prop !== 'isFundsPrimary' &&
    prop !== 'isFunds',
})(({
  theme,
  isOpen,
  isWallet,
  isOrder,
  isStockExchangeMarket,
  isPrimary,
  isHistoric,
  isFundsPrimary,
  isFunds,
}) => {
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const colouredStripe = () => {
    if (!isStockExchangeMarket)
      return {
        '&::before': {
          content: `''`,
          position: 'absolute',
          backgroundColor: theme.palette.colors.mainFocused,
          width: '4px',
          height: '100%',
          bottom: 0,
          left: 0,
          borderTopLeftRadius: '5px',
          borderBottomLeftRadius: '5px',
        },
      };

    return null;
  };

  return {
    width: '344px',
    ...getHeight({
      isOpen,
      isWallet,
      isOrder,
      isMobile,
      isPrimary,
      isFundsPrimary,
    }),
    border: `solid 1px ${theme.palette.colors.borderLine}`,
    backgroundColor: theme.palette.colors.secondaryBackground,
    padding: isOrder ? '10px 16px 10px 12px' : '2px 12px 12px 12px',
    borderRadius: '8px',
    borderTopRightRadius: '8px',
    borderBottomRightRadius: '8px',
    marginBottom: 4,
    flexDirection: isOrder && !isPrimary ? 'row' : 'column',
    display: isOrder && isPrimary ? 'grid' : 'flex',
    gridTemplateAreas:
      isHistoric && isPrimary
        ? `
        "divEsq divCentro"
        `
        : `
        "divEsq divCentro divDir"
        `,
    gap: isOrder ? '13px' : 0,
    justifyContent: isOrder ? 'space-between' : 'unset',
    position: 'relative',
    ...colouredStripe(),
    maxHeight: isFunds && '160px',
    [theme.breakpoints.up('md')]: {
      width: '603px',
      ...getHeight({ isOpen, md: true, isWallet, isOrder, isFundsPrimary }),
      padding: isOrder ? '8px 16px 16px 16px' : '2px 16px 16px 16px',
    },
    [theme.breakpoints.down('md')]: {
      gridTemplateAreas: `
      "divEsq divDir"
      "divCentro divDir"
      `,
    },
  };
});

export const WrapperMobile = styled('div', {
  shouldForwardProp: (prop) => prop !== 'isOrder',
})(({ theme, isOrder }) => ({
  display: 'flex',
  flexDirection: isOrder ? 'row' : 'column',
  gap: isOrder ? '13px' : 0,
  width: '100%',
  justifyContent: isOrder ? 'space-between' : 'unset',
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
  },
}));
export const HeaderDiv = styled('div')(() => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-end',
  gap: '10px',
}));

export const InfoDiv = styled('div')(() => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
}));

export const DataDiv = styled('div', {
  shouldForwardProp: (prop) => prop !== 'isWallet',
})(({ theme, isWallet }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-end',
  marginTop: 0,
  [theme.breakpoints.up('md')]: {
    marginTop: isWallet ? 0 : 10,
  },
}));

export const Separator = styled('div')(({ theme }) => ({
  width: '1px',
  height: '12px',
  backgroundColor: theme.palette.colors.lower,
  margin: '0 4px',
  [theme.breakpoints.up('md')]: {
    margin: '0 8px',
  },
}));

export const StockName = styled('span')(({ theme }) => ({
  fontWeight: 'bold',
  fontSize: '18px',
  color: theme.palette.colors.onSecondaryAndDetailBackground,
  textTransform: 'uppercase',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',

  [theme.breakpoints.up('md')]: {
    fontSize: 24,
  },
}));

export const TrancheDescr = styled(Typography)(({ theme }) => ({
  fontSize: '10px',
  color: theme.palette.colors.onSecondaryAndDetailBackground,
  textTransform: 'uppercase',
  marginBottom: '5px',

  [theme.breakpoints.up('md')]: {
    fontSize: '12px',
  },
}));

export const StockType = styled('span', {
  shouldForwardProp: (prop) => prop !== 'isWallet',
})(({ theme, isWallet }) => ({
  fontWeight: 'bold',
  fontSize: isWallet ? '24px' : '16px',
  color: theme.palette.colors.onSecondaryAndDetailBackground,
  marginTop: isWallet ? 0 : '8px',
  lineHeight: 'normal',
}));

export const StockInfo = styled('div', {
  shouldForwardProp: (prop) => prop !== 'isOrder',
})(({ theme, isOrder }) => ({
  display: 'flex',
  alignItems: 'center',
  fontWeight: 500,
  fontSize: '10px',
  color: theme.palette.colors.onSecondaryAndDetailBackground,
  marginBottom: isOrder ? '10px' : 5,
  textTransform: 'uppercase',
  [theme.breakpoints.up('md')]: {
    fontSize: '12px',
    marginBottom: isOrder ? 16 : 5,
    marginTop: isOrder ? 4 : 0,
  },
}));

export const WrapperColumn = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
}));

export const LineSeparator = styled('div')(({ theme }) => ({
  width: 320,
  backgroundColor: theme.palette.colors.lower,
  height: 1,
  margin: '16px auto',
  [theme.breakpoints.up('md')]: {
    width: 555,
  },
}));

export const FavoriteIcon = styled(FontAwesomeIcon)(({
  theme,
  disabled,
  color,
}) => {
  let finalColor = theme.palette.colors.star;
  if (color) {
    finalColor = theme.palette.colors[color];
  } else if (disabled) {
    finalColor = theme.palette.colors.lower;
  }

  return {
    color: finalColor,
    width: '16px',
    height: '14px',
    marginRight: '8px',
  };
});

export const WrapperHeaderName = styled('div', {
  shouldForwardProp: (prop) =>
    prop !== 'isMarketPrimary' &&
    prop !== 'isStockExchangeMarket' &&
    prop !== 'isFunds',
})(({ theme, isMarketPrimary, isStockExchangeMarket, isFunds }) => ({
  display: 'flex',
  alignItems: 'center',
  maxWidth: (isStockExchangeMarket || isFunds) && '160px',
  overflow: isMarketPrimary && 'hidden',
  [theme.breakpoints.up('md')]: {
    maxWidth: 'unset',
  },
}));

export const WrapperButtons = styled('div', {
  shouldForwardProp: (prop) => prop !== 'isWallet',
})(({ theme, isWallet }) => ({
  display: 'flex',
  gap: '8px',
  [theme.breakpoints.up('md')]: {
    position: isWallet ? 'relative' : 'initial',
    top: '12px',
  },
}));

export const WrapperStockData = styled('div')(({ theme }) => ({
  marginRight: '26px',
  display: 'flex',
  flexDirection: 'column',
  gap: 0,
  [theme.breakpoints.up('md')]: {
    gap: 16,
    flexDirection: 'row',
    marginRight: 32,
  },
}));

export const WrapperDetailWallet = styled('div')(({ theme }) => ({
  width: 'fit-content',
  margin: 'auto',
  display: 'grid',
  gridTemplateAreas: `
  "qntDisponivel vAquisicao vDiaria"
  "vNominal vAtualMoeda carteira"
  `,
  gridColumnGap: '22px',
  [theme.breakpoints.up('md')]: {
    gridTemplateAreas: `
  "qntDisponivel vAquisicao vDiaria vNominal vAtualMoeda"
  "carteira . . . ."
  `,
    gridColumnGap: '32px',
  },
}));

export const WrapperDetail = styled('div')(({ theme, id }) => ({
  display: 'grid',
  [theme.breakpoints.down('md')]: {
    gridTemplateAreas:
      id === 'divPublicOfferDetailWrapperMobile'
        ? `
          "liquidacao qMaxima documentos ."
          "aNegativa qMultiplos documentos ."
          "moeda preco documentos ."
          "tQuantidade pMinimo documentos ."
          "qMinima pMaximo documentos ."
          `
        : `
          "foundManager initialAmount documentos"
          "symbol emitente documentos"
          `,
    gridColumnGap: '15px',
  },
  [theme.breakpoints.up('md')]: {
    display: 'grid',
    gridTemplateAreas:
      id === 'divPublicOfferDetailWrapperMobile'
        ? `
          "liquidacao tQuantidade qMultiplos pMaximo documentos"
          "aNegativa qMinima preco . documentos"
          "moeda qMaxima pMinimo . documentos"
          `
        : `
          "foundManager symbol initialAmount documentos"
          "emitente . . documentos"
          `,
    gridColumnGap: '32px',
  },
}));

export const DataLabelWallet = styled('span')(({ theme }) => ({
  fontSize: '10px',
  lineHeight: 'normal',
  color: theme.palette.colors.lower,
  fontWeight: 500,
  [theme.breakpoints.up('md')]: {
    fontSize: '12px',
  },
}));

export const DataValueWallet = styled('span', {
  shouldForwardProp: (prop) =>
    prop !== 'value' && prop !== 'isOrder' && prop !== 'isUppercased',
})(({ theme, value, isOrder, isUppercased }) => {
  const getFontSize = (md) => {
    if (isOrder) {
      if (md) {
        return 12;
      }
      return 10;
    }
    if (value) {
      if (md) {
        return 18;
      }
      return 14;
    }
    if (md) {
      return 14;
    }
    return 10;
  };

  return {
    fontSize: getFontSize(),
    lineHeight: 'normal',
    fontWeight: 500,
    color: getPctgColor(value, theme),
    textTransform: isUppercased && 'uppercase',
    [theme.breakpoints.up('md')]: {
      fontSize: getFontSize('md'),
    },
  };
});

export const QuotationStyled = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'variationPctg',
})(({ theme, variationPctg }) => ({
  fontSize: '18px',
  fontWeight: 500,
  color:
    variationPctg !== null
      ? getPctgColor(variationPctg, theme)
      : theme.palette.colors.onSecondaryAndDetailBackground,
  [theme.breakpoints.down('md')]: {
    fontSize: '14px',
  },
}));

export const WrapperDataInfoWallet = styled('div', {
  shouldForwardProp: (prop) => prop !== 'isVariation',
})(({ theme, isVariation }) => ({
  display: 'flex',
  flexDirection: isVariation ? 'column' : 'row',
  gap: isVariation ? '2px' : '4px',
  [theme.breakpoints.up('md')]: {
    flexDirection: 'row',
    gap: '8px',
    alignItems: 'center',
  },
}));

export const WrapperDataWallet = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  [theme.breakpoints.up('md')]: {
    flexDirection: 'row',
    gap: '24px',
  },
}));

export const WrapperDataOrder = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  gap: '8px',
  [theme.breakpoints.up('md')]: {
    flexDirection: 'row',
    gap: '24px',
  },
}));

export const WrapperDataWalletRight = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  gap: '18px',
  [theme.breakpoints.up('md')]: {
    flexDirection: 'column-reverse',
    gap: 0,
  },
  [theme.breakpoints.down('md')]: {
    gap: 8,
  },
}));
export const WrapperDataLeftSideWallet = styled('div')(({ theme }) => ({
  display: 'flex',
  gap: '24px',
  alignItems: 'flex-end',
  [theme.breakpoints.down('md')]: {
    alignItems: 'flex-start',
  },
}));

export const WrapperStockDataRight = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-end',
}));

export const WrapperDataOrderRight = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  [theme.breakpoints.up('md')]: {
    gap: 0,
  },
}));

export const WrapperDataOrderLeft = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '10px',
  [theme.breakpoints.up('md')]: {
    flexDirection: 'row-reverse',
    alignItems: 'flex-start',
    gap: 24,
  },
}));

export const WrapperDataOrderText = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '3px',
  [theme.breakpoints.up('md')]: {
    gap: '4px',
  },
}));

export const WrapperData = styled('div')(() => ({
  display: 'flex',
  alignItems: 'flex-end',
}));

export const WrapperPills = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '9px',
  marginBottom: '8px',
  [theme.breakpoints.up('md')]: {
    gap: '16px',
    marginBottom: '6px',
  },
}));
export const WrapperFirstColumn = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
}));
export const WrapperMiddleColumn = styled('div', {
  shouldForwardProp: (prop) =>
    prop !== 'isStockExchangeMarket' && prop !== 'isFunds',
})(({ theme, isStockExchangeMarket, isFunds }) => ({
  display: 'flex',
  flexDirection: 'column',
  marginTop: (!isStockExchangeMarket || !isFunds) && '27px',
  justifyContent: (isStockExchangeMarket || isFunds) && 'flex-end',
  [theme.breakpoints.up('md')]: {
    marginTop: '8px',
  },
}));
export const WrapperButtonColumn = styled('div', {
  shouldForwardProp: (prop) =>
    prop !== 'isStockExchangeMarket' &&
    prop !== 'isPrimary' &&
    prop !== 'isFunds',
})(({ theme, isStockExchangeMarket, isPrimary, isFunds }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
  marginTop: 6,
  justifyContent: isFunds && !isPrimary ? 'space-between' : 'center',
  alignItems: (isStockExchangeMarket || isFunds) && 'flex-end',
  [theme.breakpoints.up('md')]: {
    marginTop: '8px',
  },
}));

export const FlagContainer = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
}));
